/* eslint-disable react/prop-types */
/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import InfoBanner from "../../../shared/InfoBanner";
import TopicViewer from "./TopicViewer";

interface AsyncApiChannel {
  subscribe?: Record<string, unknown>;
  publish?: Record<string, unknown>;
  parameters?: Record<string, string>;
}
interface AsyncApi {
  asyncapi: string;
  info: {
    title: string;
    version: string;
    description?: string;
    license?: {
      name: string;
      url?: string;
    };
  };
  servers?: {
    [key: string]: {
      url: string;
      description?: string;
      protocol: string;
    };
  };
  channels: {
    [key: string]: AsyncApiChannel;
  };
}

interface Channel {
  name: string;
  subscribe: boolean;
  publish: boolean;
  parameters?: Record<string, string>;
}

function mapAsyncApiTopics(asyncapi: AsyncApi) {
  const { channels } = asyncapi;
  const allTopicsList: Channel[] = Object.entries(channels).map(
    ([channelName, channel]: [string, any]) => ({
      name: channelName,
      subscribe: !!channel?.subscribe,
      publish: !!channel?.publish,
      parameters: channel.parameters,
    })
  );

  return allTopicsList;
}
export interface WebSocketViewerProps {
  asyncapi: any;
  token: string;
  apiGatewayUrl: string;
  securitySchemeType: string;
  authorizationHeader: string;
  initialEndpoint?: string;
}

export default function WebSocketViewer(props: WebSocketViewerProps) {
  const {
    asyncapi,
    token,
    apiGatewayUrl,
  } = props;

  const [allTopics, setAllTopics] = useState([
    { name: '/*', subscribe: true, publish: true, parameters: {} },
  ] as Channel[]);
  const intl = useIntl();

  useEffect(() => {
    if (asyncapi?.channels) {
      setAllTopics(mapAsyncApiTopics(asyncapi));
    }
  }, [asyncapi]);

  return (
    <>
      {token ? (
        <Box>
          {allTopics.map(({ name, publish, subscribe, parameters }: Channel) => (
            <TopicViewer
              key={name}
              token={token}
              apiEndpoint={apiGatewayUrl}
              topic={name}
              publish={publish}
              subscribe={subscribe}
              parameters={parameters}
              payload='{ "message": "Hello Server" }'
            />
          ))}
        </Box>
      ) : (
        <Box pl={1.5}>
          <InfoBanner
            message={intl.formatMessage({
              id: "components.api.ApiTryout.WebSocketViewer.toolbarButtons.generateTokenInfo",
              defaultMessage: "Generate a test key to try out the API",
            })}
            testId="generate-token-info"
          />
        </Box>
      )}
    </>
  );
}
